import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

interface NavItem {
  to: string;
  label: string;
}

interface NavItemsProps {
  items: NavItem[];
}

const Nav = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const isResourcesPage = location.pathname === "/resources";

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const leftNavItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About us" },
    { to: "/cbr-program", label: "CBR Program" },
  ];

  const rightNavItems = [
    { to: "/resources", label: "Resources" },
    { to: "/contacts", label: "Contacts" },
    { to: "/support-us", label: "Support us" },
  ];

  const NavItems: React.FC<NavItemsProps> = ({ items }) => (
    <ul className="flex flex-col gap-2 lg:flex-row items-center lg:gap-24">
      {items.map((item) => (
        <li key={item.to}>
          <Link
            to={item.to}
            onClick={() => setIsOpen(false)}
            className={`text-lg font-medium transition-colors hover:text-navYellow ${location.pathname === item.to
              ? "text-navYellow"
              : isResourcesPage
                ? "text-white"
                : "text-gray-700"
              }`}
          >
            {item.label}
          </Link>
        </li>
      ))}
    </ul>
  );

  return (
    <nav
      className={`w-full backdrop-blur-lg ${isResourcesPage ? "bg-darkerGrey" : ""
        }`}
    >
      {/* Desktop Navigation */}
      <div className="hidden lg:flex w-full px-8 py-4">
        <div className="flex w-full items-center justify-between">
          <div className="flex-1">
            <NavItems items={leftNavItems} />
          </div>

          <div className="flex-1 flex justify-center">
            <Link to="/" className="flex flex-col items-center">
              <span
                className={`text-6xl font-extrabold ${isResourcesPage ? "text-textBlue" : "text-mainBlue"
                  }`}
              >
                CBR
              </span>
              <span
                className={`text-base ${isResourcesPage ? "text-mustardYellow" : "text-gray-600"
                  }`}
              >
                Consistent Bible Reading
              </span>
            </Link>
          </div>

          <div className="flex-1 flex justify-end">
            <NavItems items={rightNavItems} />
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <div className="lg:hidden w-full">
        <div className="flex items-center justify-between px-4 py-2">
          <Link to="/" className="flex flex-col">
            <span className="text-2xl font-bold text-mainBlue">CBR</span>
            <span className="text-xs text-gray-600">
              Consistent Bible Reading
            </span>
          </Link>

          <button
            className="p-2 text-gray-700 hover:text-gray-900"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                className="h-6 w-6"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="w-full px-4 py-4 border-t border-gray-100">
            <div className="flex flex-col gap-2">
              <NavItems items={leftNavItems} />
              <NavItems items={rightNavItems} />
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Nav;
